import { useEffect, useState, useRef } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import {
    createSubmission,
    type ModuleKey,
    type Submission,
} from '../../mentorshipService';
import './SubmissionModal.css';

const MODULE_OPTIONS: { value: ModuleKey; label: string }[] = [
    { value: 'concept', label: 'Concept / Due Diligence' },
    { value: 'design', label: 'Design' },
    { value: 'authorities', label: 'Authorities' },
    { value: 'contract', label: 'Contract' },
    { value: 'tender', label: 'Tender' },
    { value: 'construction', label: 'Construction' },
];

const SOFT_WARN_LENGTH = 20_000;
const HARD_LIMIT = 50_000;

type Props = {
    userId: number;
    onClose: () => void;
    onSubmitted: (submission: Submission) => void;
};

function SubmissionModal({ userId, onClose, onSubmitted }: Props) {
    const [module, setModule] = useState<ModuleKey>('concept');
    const [content, setContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Idempotency UUID generated once on mount; reused if the user retries
    // the submit (e.g. flaky network) so the backend deduplicates.
    const clientSubmissionIdRef = useRef<string>(crypto.randomUUID());

    // localStorage draft autosave — keyed by (user_id, module). Cleared on
    // successful submit. Lets the user close the modal mid-write without
    // losing what they typed.
    const draftKey = `mentorship.draft.${userId}.${module}`;

    // Hydrate draft when the module changes
    useEffect(() => {
        const saved = window.localStorage.getItem(draftKey);
        setContent(saved ?? '');
    }, [draftKey]);

    // Save on every keystroke (debounced via React's batching is enough for
    // the volume; this is mentee free text, not a hot loop).
    useEffect(() => {
        if (content) {
            window.localStorage.setItem(draftKey, content);
        } else {
            window.localStorage.removeItem(draftKey);
        }
    }, [content, draftKey]);

    const trimmed = content.trim();
    const charCount = content.length;
    const tooShort = trimmed.length === 0;
    const overSoftLimit = charCount > SOFT_WARN_LENGTH;
    const overHardLimit = charCount > HARD_LIMIT;
    const canSubmit = !tooShort && !overHardLimit && !isSaving;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSaving(true);
        setError(null);
        try {
            const submission = await createSubmission(
                module,
                trimmed,
                clientSubmissionIdRef.current,
            );
            // Clear draft on success
            window.localStorage.removeItem(draftKey);
            onSubmitted(submission);
        } catch (err: unknown) {
            console.error('[SubmissionModal] failed to submit', err);
            // Try to detect 409 → existing submission
            // (axios errors carry response.data; mentorshipService throws raw)
            const e = err as { response?: { status?: number; data?: { detail?: string; submission?: Submission } } };
            if (e?.response?.status === 409 && e.response.data?.submission) {
                onSubmitted(e.response.data.submission);
                window.localStorage.removeItem(draftKey);
                return;
            }
            const msg =
                e?.response?.data?.detail ??
                (err instanceof Error ? err.message : 'Submission failed');
            setError(msg);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal open={true} onClose={onClose} size="lg" ariaLabel="New submission">
            <Modal.Header onClose={onClose}>New submission</Modal.Header>
            <Modal.Body>
                <div className="submission-form__field">
                    <label className="submission-form__label" htmlFor="sub-module">
                        Module
                    </label>
                    <p className="submission-form__help">
                        Pick the framework module you're reflecting on this quarter.
                    </p>
                    <select
                        id="sub-module"
                        className="submission-form__select"
                        value={module}
                        onChange={(e) => setModule(e.target.value as ModuleKey)}
                        disabled={isSaving}
                    >
                        {MODULE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="submission-form__field">
                    <label className="submission-form__label" htmlFor="sub-content">
                        Your response
                    </label>
                    <p className="submission-form__help">
                        Walk through your experience with this module. Concrete examples
                        beat vague generalities. Auto-saved as a draft until you submit.
                    </p>
                    <textarea
                        id="sub-content"
                        className="submission-form__textarea"
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Start writing…"
                        rows={14}
                        disabled={isSaving}
                    />
                    <div className="submission-form__footer-row">
                        <span
                            className={`submission-form__counter ${
                                overHardLimit
                                    ? 'is-error'
                                    : overSoftLimit
                                    ? 'is-warn'
                                    : ''
                            }`}
                        >
                            {charCount.toLocaleString()} / {HARD_LIMIT.toLocaleString()} chars
                            {overSoftLimit && !overHardLimit && ' — getting long'}
                            {overHardLimit && ' — too long, will be trimmed'}
                        </span>
                    </div>
                </div>

                {error && <p className="submission-form__error">{error}</p>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" size="md" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                >
                    {isSaving ? 'Submitting…' : 'Submit'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default SubmissionModal;
